import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useT } from "../LangContext";
import { C } from "../../lib/colors";

// ─── Fase 16B · Bloque 4 — Password reset landing page ────────────────────
// User arrives here from the Supabase recovery email link. Supabase already
// set a temporary session; we just need to capture the new password.
export default function ResetPassword() {
  const { t } = useT();
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [error, setError] = useState("");
  const [done,  setDone]  = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = t("reset_title"); }, [t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t("reset_too_short")); return; }
    if (password !== confirm) { setError(t("reset_mismatch")); return; }
    setLoading(true);
    try {
      await updatePassword(password);
      setDone(true);
      setTimeout(() => navigate("/app"), 1800);
    } catch (err) {
      setError(err?.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-page"
         style={{ minHeight: "100vh", background: C.ink, color: C.linen,
                   display: "flex", alignItems: "center", justifyContent: "center",
                   padding: 24 }}>
      <div style={{ maxWidth: 380, width: "100%", padding: 28,
                     border: `1px solid ${C.dust}`, background: "rgba(42, 48, 50, 0.40)",
                     fontFamily: "var(--plex)" }}>
        <div style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 10,
                       letterSpacing: 0.25, marginBottom: 12 }}>
          {t("reset_kicker")}
        </div>
        <h2 style={{ color: C.bone, fontSize: 20, margin: "0 0 18px",
                       fontFamily: "var(--serif), 'Fraunces', serif", fontWeight: 400 }}>
          {t("reset_title")}
        </h2>
        {done ? (
          <div data-testid="reset-done"
               style={{ color: "#A8C29E", fontSize: 13, lineHeight: 1.6 }}>
            {t("reset_done")}
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <label style={{ display: "block", marginBottom: 4, color: C.smoke,
                              fontSize: 11, letterSpacing: 0.15 }}>{t("reset_new")}</label>
            <input data-testid="reset-pw"
                   type="password" value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   autoComplete="new-password"
                   style={inputStyle} />
            <label style={{ display: "block", margin: "12px 0 4px", color: C.smoke,
                              fontSize: 11, letterSpacing: 0.15 }}>{t("reset_confirm")}</label>
            <input data-testid="reset-pw2"
                   type="password" value={confirm}
                   onChange={(e) => setConfirm(e.target.value)}
                   autoComplete="new-password"
                   style={inputStyle} />
            {error && (
              <div data-testid="reset-error"
                   style={{ marginTop: 10, color: C.cinnabar, fontSize: 12 }}>{error}</div>
            )}
            <button data-testid="reset-submit"
                    type="submit" disabled={loading}
                    style={{ marginTop: 18, width: "100%", padding: "10px 14px",
                              background: "transparent", border: `1px solid ${C.brass}`,
                              color: C.brass, cursor: "pointer",
                              fontFamily: "var(--mono)", fontSize: 12, letterSpacing: 0.18,
                              opacity: loading ? 0.5 : 1 }}>
              {loading ? "..." : t("reset_submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 10px",
  background: "rgba(0,0,0,0.30)", border: `1px solid ${C.dust}`,
  color: C.linen, fontFamily: "var(--mono)", fontSize: 13,
  boxSizing: "border-box",
};
